// components/RouterComponent.js
import { Suspense, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { routesConfig } from "config/routesConfig";
import { SkeletonTheme } from "react-loading-skeleton";
import PageLoader from "components/shared/loaders/PageLoader";
import { ErrorBoundary } from "react-error-boundary";
import { motion, AnimatePresence } from "framer-motion";
import { 
  RefreshCw, 
  Home, 
  AlertTriangle, 
  XCircle,
  Bug,
  ExternalLink,
  Zap
} from "lucide-react";

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getErrorType = () => {
    if (!error) return "unknown";
    if (error.message?.includes("network") || error.message?.includes("fetch")) 
      return "network";
    if (error.message?.includes("timeout") || error.message?.includes("timed out")) 
      return "timeout";
    if (error.message?.includes("auth") || error.message?.includes("unauthorized")) 
      return "auth";
    return "application";
  };

  const errorType = getErrorType();
  
  const errorConfig = {
    network: {
      icon: <XCircle className="w-16 h-16" />,
      title: "Connection Lost",
      message: "Unable to connect to the server. Please check your internet connection.",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200"
    },
    timeout: {
      icon: <AlertTriangle className="w-16 h-16" />,
      title: "Request Timeout",
      message: "The request took too long to complete. The server might be experiencing high traffic.",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200"
    },
    auth: {
      icon: <AlertTriangle className="w-16 h-16" />,
      title: "Session Expired",
      message: "Your session has expired. Please sign in again to continue.",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    application: {
      icon: <Bug className="w-16 h-16" />,
      title: "Unexpected Error",
      message: "Something went wrong on our end. Our team has been notified.",
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200"
    },
    unknown: {
      icon: <AlertTriangle className="w-16 h-16" />,
      title: "Oops!",
      message: "An unexpected error occurred. Please try refreshing the page.",
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200"
    }
  };

  const config = errorConfig[errorType];

  const handleReload = () => window.location.reload();
  const handleGoHome = () => window.location.href = "/";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, type: "spring", damping: 25 }}
        className="max-w-lg w-full"
      >
        {/* Error Card */}
        <div className={`${config.bgColor} ${config.borderColor} border rounded-2xl shadow-xl overflow-hidden`}>
          {/* Header */}
          <div className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring" }}
              className={`${config.color} mx-auto mb-6 w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg`}
            >
              {config.icon}
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold text-gray-900 mb-3"
            >
              {config.title}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-gray-600 mb-8 text-lg"
            >
              {config.message}
            </motion.p>
          </div>

          {/* Error Details Toggle */}
          {error && process.env.NODE_ENV === "development" && (
            <div className="px-8 pb-4">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 bg-white/50 rounded-xl hover:bg-white/80 transition-colors group"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Bug className="w-4 h-4" />
                  Error Details
                </span>
                <motion.span
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  className="text-gray-500 group-hover:text-gray-700"
                >
                  ▼
                </motion.span>
              </button>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Error Stack
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(error.stack || error.toString())}
                          className="text-xs text-primary hover:text-pink transition-colors"
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="text-xs text-gray-800 bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto">
                        {error.stack || error.toString()}
                      </pre>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Action Buttons */}
          <div className="p-8 pt-4 bg-gradient-to-t from-white/50 to-transparent">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={resetErrorBoundary}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-all font-medium shadow-md hover:shadow-lg"
              >
                <Zap className="w-4 h-4" />
                Try Again
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleReload}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-pink transition-all font-medium shadow-md hover:shadow-lg"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleGoHome}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-medium shadow-sm hover:shadow"
              >
                <Home className="w-4 h-4" />
                Go Home
              </motion.button>
            </div>

            {/* Contact Support */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500 mb-3">
                Need help? Contact our support team
              </p>
              <div className="flex gap-3 justify-center">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  href="mailto:support@example.com"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Email Support
                  <ExternalLink className="w-3 h-3" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  href="/help"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Help Center
                  <ExternalLink className="w-3 h-3" />
                </motion.a>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            Error ID: {Math.random().toString(36).substr(2, 9).toUpperCase()}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const transformRoutes = (routes) => {
  return routes.map((route) => ({
    path: route.path,
    index: route.index || false,
    element: (
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => {
          if (window.location.pathname === "/") {
            window.location.reload();
          } else {
            window.location.href = "/";
          }
        }}
      >
        <Suspense fallback={<PageLoader />}>{route.element}</Suspense>
      </ErrorBoundary>
    ),
    children: route.children ? transformRoutes(route.children) : undefined,
    errorElement: route.errorElement,
  }));
};

export const RouterComponent = () => {
  const routes = transformRoutes(routesConfig);
  const router = createBrowserRouter(routes);
  
  return (
    <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f5f5f5">
      <RouterProvider router={router} />
    </SkeletonTheme>
  );
};